import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";

const PRIMARY = "var(--portal-theme-primary, #04abbb)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const INK = "#0f172a";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
  },
};

const surveyTextSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
    fontSize: 15,
    minHeight: 48,
    color: INK,
    transition: "border-color 0.15s, box-shadow 0.15s",
    "& fieldset": { borderColor: "#d0d5dd", borderWidth: 1 },
    "&:hover": { bgcolor: "#fff" },
    "&:hover fieldset": { borderColor: "#98a2b3" },
    "&.Mui-focused": {
      bgcolor: "#fff",
      boxShadow: `0 0 0 3px color-mix(in srgb, ${PRIMARY} 18%, transparent)`,
    },
    "&.Mui-focused fieldset": {
      borderColor: `${PRIMARY} !important`,
      borderWidth: 1.5,
    },
  },
  "& .MuiOutlinedInput-input": {
    py: "13px",
    px: "14px",
    height: "auto",
    boxSizing: "border-box",
    color: INK,
    WebkitTextFillColor: INK,
    caretColor: INK,
    "&::placeholder": {
      color: "#98a2b3",
      opacity: 1,
      WebkitTextFillColor: "#98a2b3",
    },
  },
  "& .MuiOutlinedInput-root.MuiInputBase-multiline": {
    minHeight: "auto",
    py: 0.5,
  },
  "& .MuiFormHelperText-root": {
    mx: 0,
    mt: 0.75,
  },
};

/** Compact dark inputs for GymRite lead / check-in form shell */
const leadTextSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "9px",
    bgcolor: "#1d1f1d",
    fontSize: 14,
    minHeight: 44,
    color: "#fff",
    "& fieldset": { borderColor: "#3a3c3a", borderWidth: 1 },
    "&:hover fieldset": { borderColor: "#4a4c4a" },
    "&.Mui-focused fieldset": {
      borderColor: "#ff670a",
      borderWidth: 1.5,
    },
  },
  "& .MuiOutlinedInput-input": {
    py: "11px",
    px: "12px",
    height: "auto",
    boxSizing: "border-box",
    color: "#fff",
    WebkitTextFillColor: "#fff",
    caretColor: "#fff",
    "&::placeholder": { color: "#888", opacity: 1, WebkitTextFillColor: "#888" },
  },
  "& .MuiOutlinedInput-input:-webkit-autofill, & .MuiOutlinedInput-input:-webkit-autofill:hover, & .MuiOutlinedInput-input:-webkit-autofill:focus":
    {
      WebkitTextFillColor: "#fff !important",
      caretColor: "#fff",
      WebkitBoxShadow: "0 0 0 1000px #1d1f1d inset",
      transition: "background-color 9999s ease-in-out 0s",
    },
  "& .MuiOutlinedInput-root.MuiInputBase-multiline": {
    minHeight: "auto",
    py: 0.5,
  },
  "& .MuiFormHelperText-root": {
    mx: 0,
    mt: 0.5,
  },
  "& .MuiSelect-icon": { color: "#aaa" },
  "& .MuiSelect-select": { color: "#fff", WebkitTextFillColor: "#fff" },
};

const controlSx = {
  color: "#98a2b3",
  "&.Mui-checked": { color: PRIMARY },
  p: 0.75,
};

const labelSx = {
  mr: { xs: 1.5, md: 2.5 },
  ml: 0,
  alignItems: "center",
  "& .MuiFormControlLabel-label": {
    fontSize: 14.5,
    fontWeight: 500,
    color: INK,
    userSelect: "none",
  },
};

const FEELING_SCALE = [
  { value: "5", label: "Great", emoji: "😍" },
  { value: "4", label: "Good", emoji: "🙂" },
  { value: "3", label: "Ok", emoji: "😐" },
  { value: "2", label: "Bad", emoji: "🙁" },
  { value: "1", label: "Terrible", emoji: "😢" },
];

const HorizontalRadios = ({ options, value, onChange }) => (
  <Box
    role="radiogroup"
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, minmax(0, 1fr))",
        md: "repeat(3, minmax(0, 1fr))",
      },
      gap: 1,
      width: "100%",
    }}
  >
    {options.map((opt) => {
      const key = String(opt);
      const active = String(value || "") === key;
      return (
        <Box
          key={key}
          role="radio"
          aria-checked={active}
          tabIndex={0}
          onClick={() => onChange(key)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onChange(key);
            }
          }}
          sx={{
            px: 1.5,
            py: 1.1,
            borderRadius: "8px",
            border: `1px solid ${active ? PRIMARY : "#d8dee6"}`,
            bgcolor: active
              ? "color-mix(in srgb, var(--portal-theme-primary, #04abbb) 9%, white)"
              : "#fff",
            color: active ? PRIMARY : INK,
            fontSize: 14,
            fontWeight: active ? 600 : 400,
            cursor: "pointer",
            userSelect: "none",
            lineHeight: 1.35,
            transition: "border-color 0.12s, background 0.12s, color 0.12s",
            "&:hover": {
              borderColor: PRIMARY,
            },
          }}
        >
          {key}
        </Box>
      );
    })}
  </Box>
);

const HorizontalChecks = ({ options, value, onChange }) => {
  const selected = Array.isArray(value) ? value : [];
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          md: "repeat(3, minmax(0, 1fr))",
        },
        gap: 0.5,
        width: "100%",
      }}
    >
      {options.map((opt) => {
        const key = String(opt);
        const checked = selected.includes(key);
        return (
          <FormControlLabel
            key={key}
            control={
              <Checkbox
                checked={checked}
                onChange={(e) => {
                  if (e.target.checked) onChange([...selected, key]);
                  else onChange(selected.filter((v) => v !== key));
                }}
                sx={controlSx}
              />
            }
            label={key}
            sx={labelSx}
          />
        );
      })}
    </Box>
  );
};

const FeelingCards = ({ value, onChange }) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      gap: 1.25,
    }}
  >
    {FEELING_SCALE.map((item) => {
      const active = String(value) === item.value || String(value) === item.label;
      return (
        <Box
          key={item.value}
          role="button"
          tabIndex={0}
          onClick={() => onChange(item.label)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onChange(item.label);
            }
          }}
          sx={{
            width: { xs: 72, sm: 84 },
            minHeight: 88,
            borderRadius: "12px",
            border: `2px solid ${active ? PRIMARY : "#e4e7ec"}`,
            bgcolor: "#fff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            cursor: "pointer",
            transition: "border-color 0.15s ease, box-shadow 0.15s ease",
            boxShadow: active ? `0 0 0 1px ${PRIMARY}` : "none",
            "&:hover": { borderColor: PRIMARY },
          }}
        >
          <Box sx={{ fontSize: 28, lineHeight: 1 }}>{item.emoji}</Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: INK }}>
            {item.label}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const ScaleRow = ({ value, onChange, min = 1, max = 10 }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
    {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => {
      const active = String(value) === String(n);
      return (
        <Box
          key={n}
          role="button"
          tabIndex={0}
          onClick={() => onChange(String(n))}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onChange(String(n));
            }
          }}
          sx={{
            width: 44,
            height: 44,
            borderRadius: "10px",
            border: `2px solid ${active ? PRIMARY : "#e4e7ec"}`,
            bgcolor: active ? PRIMARY : "#fff",
            color: active ? "#fff" : INK,
            display: "grid",
            placeItems: "center",
            fontWeight: 600,
            fontSize: 15,
            cursor: "pointer",
            "&:hover": { borderColor: PRIMARY },
          }}
        >
          {n}
        </Box>
      );
    })}
  </Box>
);

const looksLikeFeelingQuestion = (label = "") => {
  const t = String(label).toLowerCase();
  return (
    t.includes("how do you feel") ||
    t.includes("feeling") ||
    t.includes("sentiment") ||
    t.includes("mood")
  );
};

/** Renders one public form question control. */
export default function PublicTrackerField({
  question,
  value,
  error,
  onChange,
  surveyMode = false,
  hideLabel = false,
  leadMode = false,
  portalFlat = false,
  inputId,
}) {
  const fieldType = question?.fieldType || "short_text";
  const label = question?.label || question?.statement || "Question";
  const placeholder = question?.placeholder || "";
  const required = Boolean(question?.required);
  const options = Array.isArray(question?.options) ? question.options : [];
  const id = inputId || `survey-input-${question?.id || "field"}`;
  const textSx = leadMode ? leadTextSx : surveyTextSx;
  const leadPlaceholder = leadMode
    ? placeholder ||
      (String(label).length > 32
        ? "Your answer"
        : String(label).replace(/\s*\*$/, "").toLowerCase())
    : placeholder;

  const errorLine = error ? (
    <Typography sx={{ fontSize: 12.5, color: "#dc2626", mt: 0.75 }}>
      {error}
    </Typography>
  ) : null;

  if (surveyMode) {
    if (
      !portalFlat &&
      (fieldType === "scale" || fieldType === "multiple_choice") &&
      looksLikeFeelingQuestion(label) &&
      (!options.length || options.length === 5)
    ) {
      return (
        <Box>
          <FeelingCards value={value} onChange={onChange} />
          {errorLine}
        </Box>
      );
    }

    if (fieldType === "yes_no") {
      return (
        <Box>
          <HorizontalRadios
            options={["Yes", "No"]}
            value={
              value === "yes" ? "Yes" : value === "no" ? "No" : value || ""
            }
            onChange={(v) =>
              onChange(String(v).toLowerCase() === "yes" ? "yes" : "no")
            }
          />
          {errorLine}
        </Box>
      );
    }

    if (fieldType === "multiple_choice" || fieldType === "dropdown" || fieldType === "status" || fieldType === "gym" || fieldType === "programme" || fieldType === "coach") {
      return (
        <Box>
          <HorizontalRadios
            options={options.length ? options : ["Option 1", "Option 2"]}
            value={value || ""}
            onChange={onChange}
          />
          {errorLine}
        </Box>
      );
    }

    if (fieldType === "checkbox") {
      return (
        <Box>
          <HorizontalChecks
            options={options.length ? options : ["Option 1", "Option 2"]}
            value={Array.isArray(value) ? value : []}
            onChange={onChange}
          />
          {errorLine}
        </Box>
      );
    }

    if (fieldType === "scale") {
      if (!options.length) {
        return (
          <Box>
            {portalFlat ? (
              <ScaleRow value={value} onChange={onChange} min={1} max={5} />
            ) : (
              <FeelingCards value={value} onChange={onChange} />
            )}
            {errorLine}
          </Box>
        );
      }
      return (
        <Box>
          <ScaleRow value={value} onChange={onChange} />
          {errorLine}
        </Box>
      );
    }

    if (fieldType === "long_text") {
      return (
        <TextField
          id={id}
          fullWidth
          multiline
          minRows={leadMode ? 3 : 4}
          placeholder={leadPlaceholder || placeholder || "Type your answer…"}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          error={Boolean(error)}
          helperText={error || ""}
          sx={textSx}
        />
      );
    }

    if (fieldType === "date" || fieldType === "review_date" || fieldType === "follow_up_date") {
      return (
        <TextField
          id={id}
          fullWidth
          type="date"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          error={Boolean(error)}
          helperText={error || ""}
          InputLabelProps={{ shrink: true }}
          sx={{ ...textSx, maxWidth: 280 }}
        />
      );
    }

    if (fieldType === "time") {
      return (
        <TextField
          id={id}
          fullWidth
          type="time"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          error={Boolean(error)}
          helperText={error || ""}
          InputLabelProps={{ shrink: true }}
          sx={{ ...textSx, maxWidth: 200 }}
        />
      );
    }

    if (fieldType === "image_upload" || fieldType === "file_upload") {
      return (
        <TextField
          id={id}
          fullWidth
          placeholder="e.g. photo coming soon"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          error={Boolean(error)}
          helperText={error || "File upload is demo-only — type a note instead."}
          sx={textSx}
        />
      );
    }

    const inputType =
      fieldType === "email"
        ? "email"
        : fieldType === "phone"
          ? "tel"
          : fieldType === "number"
            ? "number"
            : "text";

    return (
      <TextField
        id={id}
        fullWidth
        type={inputType}
        placeholder={
          leadPlaceholder ||
          placeholder ||
          (fieldType === "email"
            ? "you@email.com"
            : fieldType === "phone"
              ? "Phone number"
              : "Type your answer…")
        }
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        error={Boolean(error)}
        helperText={error || ""}
        sx={textSx}
      />
    );
  }

  // Legacy compact mode (kept for any non-survey use)
  const common = {
    fullWidth: true,
    size: "medium",
    label: hideLabel ? undefined : label,
    required,
    error: Boolean(error),
    helperText: error || "",
    sx: fieldSx,
  };

  if (fieldType === "long_text") {
    return (
      <TextField
        {...common}
        multiline
        minRows={3}
        placeholder={placeholder}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (fieldType === "number" || fieldType === "scale") {
    return (
      <TextField
        {...common}
        type="number"
        placeholder={placeholder}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        inputProps={
          fieldType === "scale" ? { min: 1, max: 10, step: 1 } : undefined
        }
      />
    );
  }

  if (fieldType === "email") {
    return (
      <TextField
        {...common}
        type="email"
        placeholder={placeholder || "you@email.com"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (fieldType === "phone") {
    return (
      <TextField
        {...common}
        type="tel"
        placeholder={placeholder || "Phone number"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (fieldType === "date" || fieldType === "review_date" || fieldType === "follow_up_date") {
    return (
      <TextField
        {...common}
        type="date"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
    );
  }

  if (fieldType === "time") {
    return (
      <TextField
        {...common}
        type="time"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
    );
  }

  if (fieldType === "yes_no") {
    return (
      <Box>
        {!hideLabel ? (
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: INK, mb: 0.75 }}>
            {label}
            {required ? " *" : ""}
          </Typography>
        ) : null}
        <RadioGroup row value={value || ""} onChange={(e) => onChange(e.target.value)}>
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="no" control={<Radio />} label="No" />
        </RadioGroup>
        {errorLine}
      </Box>
    );
  }

  if (
    fieldType === "dropdown" ||
    fieldType === "status" ||
    fieldType === "gym" ||
    fieldType === "programme" ||
    fieldType === "coach"
  ) {
    return (
      <TextField
        {...common}
        select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      >
        <MenuItem value="">
          <em>{placeholder || "Select…"}</em>
        </MenuItem>
        {(options.length ? options : ["Option 1", "Option 2"]).map((opt) => (
          <MenuItem key={String(opt)} value={String(opt)}>
            {String(opt)}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (fieldType === "multiple_choice") {
    return (
      <Box>
        {!hideLabel ? (
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: INK, mb: 0.75 }}>
            {label}
            {required ? " *" : ""}
          </Typography>
        ) : null}
        <RadioGroup value={value || ""} onChange={(e) => onChange(e.target.value)}>
          {(options.length ? options : ["Option 1", "Option 2"]).map((opt) => (
            <FormControlLabel
              key={String(opt)}
              value={String(opt)}
              control={<Radio />}
              label={String(opt)}
            />
          ))}
        </RadioGroup>
        {errorLine}
      </Box>
    );
  }

  if (fieldType === "checkbox") {
    const selected = Array.isArray(value) ? value : [];
    return (
      <Box>
        {!hideLabel ? (
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: INK, mb: 0.75 }}>
            {label}
            {required ? " *" : ""}
          </Typography>
        ) : null}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          {(options.length ? options : ["Option 1", "Option 2"]).map((opt) => {
            const checked = selected.includes(String(opt));
            return (
              <FormControlLabel
                key={String(opt)}
                control={
                  <Checkbox
                    checked={checked}
                    onChange={(e) => {
                      if (e.target.checked) onChange([...selected, String(opt)]);
                      else onChange(selected.filter((v) => v !== String(opt)));
                    }}
                    sx={{ color: PRIMARY, "&.Mui-checked": { color: PRIMARY } }}
                  />
                }
                label={String(opt)}
              />
            );
          })}
        </Box>
        {errorLine}
      </Box>
    );
  }

  if (fieldType === "image_upload" || fieldType === "file_upload") {
    return (
      <Box
        sx={{
          border: `1.5px dashed ${BORDER}`,
          borderRadius: "10px",
          p: 2,
          bgcolor: "#f8fafc",
        }}
      >
        {!hideLabel ? (
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: INK }}>
            {label}
            {required ? " *" : ""}
          </Typography>
        ) : null}
        <Typography sx={{ mt: 0.5, fontSize: 13, color: MUTED }}>
          File upload is demo-only — type a note instead.
        </Typography>
        <TextField
          fullWidth
          size="small"
          sx={{ ...fieldSx, mt: 1.25 }}
          placeholder="e.g. photo coming soon"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          error={Boolean(error)}
          helperText={error || ""}
        />
      </Box>
    );
  }

  return (
    <TextField
      {...common}
      placeholder={placeholder}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export const validatePublicAnswers = (questions, answers) => {
  const errors = {};
  (questions || []).forEach((q) => {
    if (!q?.required) return;
    const value = answers[q.id];
    const emptyArray = Array.isArray(value) && value.length === 0;
    if (value == null || value === "" || emptyArray) {
      errors[q.id] = "Required";
    }
  });
  return errors;
};

export const usePublicFormState = (questions, seedAnswers = null) => {
  const seedKey = seedAnswers ? JSON.stringify(seedAnswers) : "";
  const initial = useMemo(() => {
    const next = {};
    (questions || []).forEach((q) => {
      next[q.id] = q.fieldType === "checkbox" ? [] : "";
    });
    if (seedAnswers && typeof seedAnswers === "object") {
      Object.keys(next).forEach((id) => {
        const seeded = seedAnswers[id];
        if (seeded == null || seeded === "") return;
        next[id] = seeded;
      });
    }
    return next;
  }, [questions, seedKey]);

  const [answers, setAnswers] = useState(initial);
  const [errors, setErrors] = useState({});

  const questionKey = (questions || []).map((q) => q.id).join("|");
  useEffect(() => {
    setAnswers(initial);
    setErrors({});
  }, [questionKey, initial]);

  const setAnswer = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const validate = () => {
    const nextErrors = validatePublicAnswers(questions, answers);
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  return { answers, setAnswer, errors, validate, setErrors, setAnswers };
};
